import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Milk, Loader2 } from "lucide-react";

// Lokal ausgeliefertes Hero-Bild (DSGVO: keine externen CDN-Anfragen)
const HERO = `${process.env.PUBLIC_URL || ""}/login-hero.jpg`;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(errMsg(err, "Anmeldung fehlgeschlagen."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Alpenlandschaft Österreich" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="text-3xl font-semibold" style={{ fontFamily: "var(--font-heading)" }}>
            Milchgeld. Proben. Klarheit.
          </h2>
          <p className="mt-2 text-white/85 max-w-md">
            Das Lieferantenportal der Molkerei Seifried GmbH – Liefermengen, Laborwerte und
            Abrechnungen jederzeit im Blick.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
              <Milk className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold" style={{ fontFamily: "var(--font-heading)" }}>seifried.app</h1>
              <p className="text-sm text-muted-foreground">Molkerei Seifried GmbH · Lieferantenportal</p>
            </div>
          </div>

          <Card className="shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-1">Anmeldung</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Zugriff auf Liefermengen, Milchproben und Abrechnungen.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@hof.at"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Passwort</Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
                {error && (
                  <p data-testid="login-error-message" className="text-sm text-destructive">
                    {error}
                  </p>
                )}
                <Button data-testid="login-submit-button" type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Anmelden
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Zugangsdaten erhalten Sie von der Molkerei Seifried GmbH.
          </p>

          <div className="mt-4 flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <Link to="/impressum" className="hover:text-primary hover:underline" data-testid="login-impressum-link">
              Impressum
            </Link>
            <span className="text-border">·</span>
            <Link to="/datenschutz" className="hover:text-primary hover:underline" data-testid="login-datenschutz-link">
              Datenschutz
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
